import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { isDateAllowedByScheduleOverview, toLocalDateString, type ScheduleOverviewRules } from "@/lib/schedule-overview";
import type { DayOverrideMarker } from "@/lib/override-periods";
import type { CalendarSession, ViewMode } from "./types";
import { STATUS_STYLES, effectiveStatus, fmtDate, isSameDay, parseTime } from "./types";

interface CalendarGridProps {
  days: Date[];
  view: ViewMode;
  anchor: Date;
  today: Date;
  selectedDate: Date | null;
  sessionsByDate: Record<string, CalendarSession[]>;
  onSelectDate: (date: Date) => void;
  showTherapist?: boolean;
  availabilityRules?: ScheduleOverviewRules | null;
  /** Therapist calendar exceptions per YYYY-MM-DD (admin view of one therapist). */
  dayMarkers?: Record<string, DayOverrideMarker[]>;
}

const MARKER_STYLES: Record<DayOverrideMarker["kind"], { chip: string; dot: string }> = {
  off: { chip: "border-rose-200 bg-rose-50 text-rose-700", dot: "bg-rose-500" },
  block: { chip: "border-amber-200 bg-amber-50 text-amber-700", dot: "bg-amber-500" },
  add: { chip: "border-emerald-200 bg-emerald-50 text-emerald-700", dot: "bg-emerald-500" },
};

export const DAY_MARKER_LEGEND: Array<{ kind: DayOverrideMarker["kind"]; label: string; dot: string }> = [
  { kind: "off", label: "Ne radi", dot: MARKER_STYLES.off.dot },
  { kind: "block", label: "Blokiran dio dana", dot: MARKER_STYLES.block.dot },
  { kind: "add", label: "Dodatni termini", dot: MARKER_STYLES.add.dot },
];

function DayMarkerChips({ markers, className }: { markers: DayOverrideMarker[]; className?: string }) {
  if (markers.length === 0) return null;
  return (
    <div className={cn("flex min-w-0 flex-col gap-0.5", className)}>
      {markers.map((m, i) => (
        <span
          key={i}
          className={cn(
            "truncate rounded-md border px-1.5 py-0.5 text-[10px] font-medium leading-tight tabular-nums",
            MARKER_STYLES[m.kind].chip,
          )}
        >
          {m.label}
        </span>
      ))}
    </div>
  );
}

const isOff = (markers: DayOverrideMarker[]) => markers.some((m) => m.kind === "off");

export function CalendarGrid({
  days,
  view,
  anchor,
  today,
  selectedDate,
  sessionsByDate,
  onSelectDate,
  showTherapist = false,
  availabilityRules = null,
  dayMarkers,
}: CalendarGridProps) {
  const { t } = useTranslation();
  const dayNames = t("calendar.days", { returnObjects: true }) as string[];
  const isDateAllowed = (day: Date) =>
    !availabilityRules || isDateAllowedByScheduleOverview(day, today, availabilityRules);
  const markersFor = (day: Date) => dayMarkers?.[fmtDate(day)] ?? [];

  // Mobile week view: vertical day list
  if (view === "week") {
    return (
      <>
        {/* Desktop: 7-column grid */}
        <div className="hidden overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm md:block">
          <div className="grid grid-cols-7 border-b border-border/40 bg-muted/30">
            {dayNames.map((d) => (
              <div key={d} className="px-2 py-2.5 text-center text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((day, i) => (
              <WeekDayCell
                key={fmtDate(day)}
                day={day}
                index={i}
                totalDays={days.length}
                today={today}
                selectedDate={selectedDate}
                sessions={sessionsByDate[fmtDate(day)] || []}
                onSelect={onSelectDate}
                muted={!isDateAllowed(day)}
                markers={markersFor(day)}
                showTherapist={showTherapist}
                t={t}
              />
            ))}
          </div>
        </div>

        {/* Mobile: vertical day cards */}
        <div className="md:hidden space-y-2">
          {days.map((day) => {
            const key = fmtDate(day);
            const daySessions = sessionsByDate[key] || [];
            const isToday = isSameDay(day, today);
            const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
            const dayIdx = (day.getDay() + 6) % 7;
            const muted = !isDateAllowed(day);
            const markers = markersFor(day);

            return (
              <button
                key={key}
                data-date={key}
                onClick={() => onSelectDate(day)}
                className={cn(
                  "w-full rounded-xl border p-3 text-left shadow-sm transition-all",
                  muted && "opacity-45",
                  isSelected
                    ? "border-primary/40 bg-primary/[0.05] ring-1 ring-primary/20"
                    : "border-border/50 bg-card hover:border-border hover:bg-muted/30",
                  !isSelected && muted && "border-border/40 bg-muted/20",
                  !isSelected && isOff(markers) && "border-rose-200/70 bg-rose-50/40",
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="flex flex-col items-center">
                    <span className="text-[10px] font-medium uppercase text-muted-foreground">
                      {dayNames[dayIdx]}
                    </span>
                    <span
                      className={cn(
                        "mt-0.5 flex size-9 items-center justify-center rounded-full text-base font-semibold",
                        isToday
                          ? "bg-primary text-primary-foreground"
                          : "text-foreground"
                      )}
                    >
                      {day.getDate()}
                    </span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <DayMarkerChips markers={markers} className="mb-1 items-start" />
                    {daySessions.length === 0 ? (
                      <p className="text-xs text-muted-foreground/50">{t("calendar.noSessions")}</p>
                    ) : (
                      <div className="space-y-1">
                        {daySessions.slice(0, 3).map((s) => {
                          const style = STATUS_STYLES[effectiveStatus(s)] || STATUS_STYLES.scheduled;
                          const time = parseTime(s.scheduledAt);
                          const primaryName = showTherapist ? s.therapistName : s.clientName;
                          return (
                            <div
                              key={s.id}
                              className={cn(
                                "flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs",
                                style.bg, style.border, style.text,
                                s.status === "cancelled" && "opacity-50"
                              )}
                            >
                              <span className={cn("size-1.5 shrink-0 rounded-full", style.dot)} />
                              <span className="font-semibold tabular-nums">
                                {String(time.hour).padStart(2, "0")}:{String(time.minute).padStart(2, "0")}
                              </span>
                              <span className="truncate text-foreground/70">{primaryName}</span>
                              <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">{s.durationMinutes} min</span>
                            </div>
                          );
                        })}
                        {daySessions.length > 3 && (
                          <span className="block text-[10px] text-muted-foreground pl-1">
                            {t("calendar.more", { count: daySessions.length - 3 })}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </>
    );
  }

  // Month view (same for all screens, compact enough)
  return (
    <div className="overflow-hidden rounded-xl border border-border/60 bg-card shadow-sm">
      <div className="grid grid-cols-7 border-b border-border/40 bg-muted/30">
        {dayNames.map((d) => (
          <div key={d} className="px-1 py-2 text-center text-[10px] sm:text-[11px] font-semibold uppercase tracking-widest text-muted-foreground sm:px-2 sm:py-2.5">
            <span className="hidden sm:inline">{d}</span>
            <span className="sm:hidden">{d.charAt(0)}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day, i) => {
          const key = fmtDate(day);
          const daySessions = sessionsByDate[key] || [];
          const isToday = isSameDay(day, today);
          const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
          const isCurrentMonth = day.getMonth() === anchor.getMonth();
          const muted = !isCurrentMonth || !isDateAllowed(day);
          const markers = markersFor(day);
          const scheduledCount = daySessions.filter((s) => effectiveStatus(s) === "scheduled").length;
          const pendingOutcomeCount = daySessions.filter((s) => effectiveStatus(s) === "pending_outcome").length;
          const completedCount = daySessions.filter((s) => effectiveStatus(s) === "completed").length;
          const cancelledCount = daySessions.filter((s) => effectiveStatus(s) === "cancelled" || effectiveStatus(s) === "no_show").length;
          const holdCount = daySessions.filter((s) => effectiveStatus(s) === "hold").length;

          return (
            <button
              key={key}
              data-date={key}
              onClick={() => onSelectDate(day)}
              className={cn(
                "relative flex flex-col border-b border-r border-border/20 transition-colors min-h-[68px] p-1 sm:min-h-[112px] sm:p-1.5",
                i % 7 === 6 && "border-r-0",
                i >= days.length - 7 && "border-b-0",
                !isCurrentMonth && "bg-muted/20",
                isCurrentMonth && isOff(markers) && "bg-rose-50/50",
                muted && "opacity-45",
                isSelected && "bg-primary/[0.04] ring-1 ring-inset ring-primary/20",
                !isSelected && isCurrentMonth && "hover:bg-muted/40",
              )}
            >
              <span
                className={cn(
                  "flex size-6 sm:size-7 items-center justify-center rounded-full text-xs sm:text-sm font-medium",
                  isToday
                    ? "bg-primary text-primary-foreground font-bold"
                    : isCurrentMonth
                      ? "text-foreground"
                      : "text-muted-foreground/50"
                )}
              >
                {day.getDate()}
              </span>
              <div className="mt-0.5 flex flex-wrap items-center gap-0.5 sm:hidden">
                {markers.map((m, mi) => (
                  <span key={mi} title={m.label} className={cn("size-1.5 rounded-full", MARKER_STYLES[m.kind].dot)} />
                ))}
                {scheduledCount > 0 && (
                  <span className="flex size-4 sm:size-5 items-center justify-center rounded-full bg-blue-100 text-[8px] sm:text-[9px] font-bold text-blue-700">
                    {scheduledCount}
                  </span>
                )}
                {completedCount > 0 && (
                  <span className="flex size-4 sm:size-5 items-center justify-center rounded-full bg-emerald-100 text-[8px] sm:text-[9px] font-bold text-emerald-700">
                    {completedCount}
                  </span>
                )}
                {pendingOutcomeCount > 0 && (
                  <span className="flex size-4 sm:size-5 items-center justify-center rounded-full bg-amber-100 text-[8px] sm:text-[9px] font-bold text-amber-700">
                    {pendingOutcomeCount}
                  </span>
                )}
                {cancelledCount > 0 && (
                  <span className="flex size-4 sm:size-5 items-center justify-center rounded-full bg-red-100 text-[8px] sm:text-[9px] font-bold text-red-600">
                    {cancelledCount}
                  </span>
                )}
                {holdCount > 0 && (
                  <span className="flex size-4 items-center justify-center rounded-full bg-amber-100 text-[8px] font-bold text-amber-700">
                    {holdCount}
                  </span>
                )}
              </div>
              <div className="mt-1 hidden min-w-0 flex-1 space-y-1 overflow-hidden sm:block">
                <DayMarkerChips markers={markers} />
                {daySessions.slice(0, 2).map((s) => {
                  const style = STATUS_STYLES[effectiveStatus(s)] || STATUS_STYLES.scheduled;
                  const time = parseTime(s.scheduledAt);
                  const primaryName = showTherapist ? s.therapistName : s.clientName;
                  return (
                    <div
                      key={s.id}
                      className={cn(
                        "flex items-center gap-1.5 rounded-md border px-1.5 py-1 text-[10px] leading-tight",
                        style.bg,
                        style.border,
                        style.text,
                        s.status === "cancelled" && "opacity-50 line-through",
                      )}
                    >
                      <span className={cn("size-1.5 shrink-0 rounded-full", style.dot)} />
                      <span className="font-semibold tabular-nums">
                        {String(time.hour).padStart(2, "0")}:{String(time.minute).padStart(2, "0")}
                      </span>
                      <span className="truncate">{primaryName?.split(" ")[0]}</span>
                    </div>
                  );
                })}
                {daySessions.length > 2 && (
                  <span className="block text-[10px] text-muted-foreground">
                    {t("calendar.more", { count: daySessions.length - 2 })}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Desktop week day cell ─── */

function WeekDayCell({
  day, index, totalDays, today, selectedDate, sessions, onSelect, muted, markers, showTherapist, t,
}: {
  day: Date;
  index: number;
  totalDays: number;
  today: Date;
  selectedDate: Date | null;
  sessions: CalendarSession[];
  onSelect: (d: Date) => void;
  muted: boolean;
  markers: DayOverrideMarker[];
  showTherapist: boolean;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const isToday = isSameDay(day, today);
  const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;

  return (
    <button
      data-date={toLocalDateString(day)}
      onClick={() => onSelect(day)}
      className={cn(
        "relative flex min-h-[168px] flex-col border-b border-r border-border/20 p-2.5 text-left transition-colors",
        index % 7 === 6 && "border-r-0",
        index >= totalDays - 7 && "border-b-0",
        muted && "opacity-45",
        !isSelected && isOff(markers) && "bg-rose-50/50",
        isSelected && "bg-primary/[0.05] ring-1 ring-inset ring-primary/25",
        !isSelected && "hover:bg-muted/40",
      )}
    >
      <div className="flex items-center justify-between">
        <span
          className={cn(
            "flex size-7 items-center justify-center rounded-full text-sm font-medium",
            isToday ? "bg-primary text-primary-foreground font-bold" : "text-foreground"
          )}
        >
          {day.getDate()}
        </span>
        {sessions.length > 0 && (
          <span className="text-[10px] font-medium text-muted-foreground">{sessions.length}</span>
        )}
      </div>
      <div className="mt-2 flex-1 space-y-1.5 overflow-hidden">
        <DayMarkerChips markers={markers} />
        {sessions.slice(0, 4).map((s) => {
          const style = STATUS_STYLES[effectiveStatus(s)] || STATUS_STYLES.scheduled;
          const time = parseTime(s.scheduledAt);
          const primaryName = showTherapist ? s.therapistName : s.clientName;
          const short = primaryName?.split(" ")[0];
          return (
            <div
              key={s.id}
              className={cn(
                "flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-[10px] leading-tight shadow-[0_1px_0_rgba(0,0,0,0.02)]",
                style.bg, style.border, style.text,
                s.status === "cancelled" && "opacity-50 line-through"
              )}
            >
              <span className={cn("size-1.5 shrink-0 rounded-full", style.dot)} />
              <span className="font-semibold tabular-nums">
                {String(time.hour).padStart(2, "0")}:{String(time.minute).padStart(2, "0")}
              </span>
              <span className="truncate font-medium">{short}</span>
            </div>
          );
        })}
        {sessions.length > 4 && (
          <span className="block text-[10px] text-muted-foreground">
            {t("calendar.more", { count: sessions.length - 4 })}
          </span>
        )}
      </div>
    </button>
  );
}
